import { checkedStoreResponse } from "./storeResponse";
import axios from "axios";
import Cookies from "js-cookie";
import { withApiError, withApiErrorResponse, apiErrorMessage } from "../utils/apiError";

const baseURL = process.env.REACT_APP_BASE_BACKEND_API_URL;

export const deleteinventory = async (selectedItems) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.delete(`${baseURL}api/v1/meter-inventory/delete`, {
      headers: { Authorization: `Bearer ${token}` },
      data: selectedItems, // Use the `data` key for the request body
    });

    return withApiError(response.data);
  } catch (error) {
    console.error("Error deleting inventory:", error.response?.data || error.message);
    throw withApiErrorResponse(error); // Rethrow the error to be handled by the calling function
  }
};


// 600 = data, 607 = no data; anything else carries the backend message in body.value.
const readSearchEnvelope = (data, emptyValue) => {
  const code = data?.header?.code;
  const value = data?.body?.value;
  if (code === 607) return emptyValue;
  if (code !== undefined && code !== 600) {
    throw new Error(typeof value === "string" && value ? value : "Request failed");
  }
  return value ?? emptyValue;
};

export const searchMeterInventory = async (payload) => {
  const token = Cookies.get("accessToken");
  const response = await axios.post(`${baseURL}api/v1/meter-inventory/search`, payload, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return readSearchEnvelope(response.data, {});
};

export const getMeterInventoryColumnValues = async (payload) => {
  const token = Cookies.get("accessToken");
  const response = await axios.post(`${baseURL}api/v1/meter-inventory/column-values`, payload, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return readSearchEnvelope(response.data, []);
};

export const fetchInventory = async (
  currentPage,
  pageSize,
  fromDate,
  toDate,
  meterBarCode,
  toLocation,
  selectedOrgId,
  // projectId
) => {
  const token = Cookies.get("accessToken");

  const params = new URLSearchParams({
    page: currentPage,
    size: pageSize,
    organizationId: selectedOrgId,
    // projectId
  });

  const formatDateTime = (date) => {
    if (!date) return "";
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    const hours = String(localDate.getHours()).padStart(2, "0");
    const minutes = String(localDate.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formattedFromDate = formatDateTime(fromDate);
  const formattedToDate = formatDateTime(toDate);

  if (formattedFromDate) params.append("fromDate", formattedFromDate);
  if (formattedToDate) params.append("toDate", formattedToDate);
  if (meterBarCode) params.append("meterBarCode", meterBarCode);
  if (toLocation) params.append("toLocation", toLocation);

  try {
    const response = await axios.get(
      `${baseURL}api/v1/meter-inventory/get-list?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("API Response:", response.data);

    return response.data?.body?.value || [];
  } catch (error) {
    console.error("Error fetching inventory data:", error);
    return [];
  }
};

export const getMeterInventory = async (barcode, selectedOrgId) => {
  const token = Cookies.get("accessToken");
  const userType = Cookies.get("userType");

  try {
    let params = {
      meterBarCode: barcode,
    };

    if (userType === "super_admin") {
      params.organizationId = selectedOrgId;
    }

    const response = await axios.get(`${baseURL}api/v1/meter-inventory/get`, {
      params: params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching meter inventory:", error);
    throw withApiErrorResponse(error);
  }
};

export const updateInventory = async (meterDetails) => {
  const token = Cookies.get("accessToken");
  const filteredDetails = Object.fromEntries(
    Object.entries(meterDetails).filter(([key, value]) => value !== null)
  );

  try {
    const response = await axios.post(
      `${baseURL}api/v1/meter-inventory/update-meter-record`,
      { ...filteredDetails },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.header.code === 600) {
      return true;
    } else {
      const backendError = new Error("Failed to update meter record");
      backendError.body = response.data.body;
      throw backendError;
    }
  } catch (error) {
    console.error("Error updating meter record:", error.message);
    throw withApiErrorResponse(error);
  }
};

export const generateInventoryReport = async (
  selectedOrgId,
  startDate,
  endDate
) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/meter-inventory/generate-report`,
      {
        params: {
          organizationId: selectedOrgId,
          startDate: `${startDate}T00:00:00`,
          endDate: `${endDate}T00:00:00`,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error while generating report:", error);
    throw withApiErrorResponse(error);
  }
};

export const createStoreApi = async ({
  projectId,
  organizationId,
  storeName,
  name,
  latitude,
  longitude,
  storeStatus,
  status,
}) => {
  const token = Cookies.get("accessToken") || localStorage.getItem("accessToken") || localStorage.getItem("token");
  try {
    const rawStatus = storeStatus || status || "ACTIVE";
    const finalStoreStatus = String(rawStatus).toUpperCase() === "INACTIVE" ? "INACTIVE" : "ACTIVE";

    const payload = {
      projectId: Number(projectId),
      ...(organizationId != null ? { organizationId: Number(organizationId) } : {}),
      storeName: String(storeName || name || "").trim(),
      latitude: Number(latitude),
      longitude: Number(longitude),
      storeStatus: finalStoreStatus,
    };

    const response = await axios.post(
      `${baseURL}api/v1/meter-inventory/store/create`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return checkedStoreResponse(response);
  } catch (error) {
    console.error("Error creating store:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const updateStoreApi = async ({
  id,
  projectId,
  organizationId,
  storeName,
  name,
  latitude,
  longitude,
  storeStatus,
  status,
}) => {
  const token = Cookies.get("accessToken") || localStorage.getItem("accessToken") || localStorage.getItem("token");
  try {
    const rawStatus = storeStatus || status || "ACTIVE";
    const finalStoreStatus = String(rawStatus).toUpperCase() === "INACTIVE" ? "INACTIVE" : "ACTIVE";

    const payload = {
      id: Number(id),
      projectId: Number(projectId),
      ...(organizationId != null ? { organizationId: Number(organizationId) } : {}),
      storeName: String(storeName || name || "").trim(),
      latitude: Number(latitude),
      longitude: Number(longitude),
      storeStatus: finalStoreStatus,
    };

    const response = await axios.post(
      `${baseURL}api/v1/meter-inventory/store/update`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return checkedStoreResponse(response);
  } catch (error) {
    console.error("Error updating store:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const getStoresByProjectIdApi = async (projectId) => {
  const token = Cookies.get("accessToken") || localStorage.getItem("accessToken") || localStorage.getItem("token");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/meter-inventory/store`,
      {
        params: { projectId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return checkedStoreResponse(response);
  } catch (error) {
    console.error("Error fetching stores by projectId:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const getStoreListApi = async ({
  projectId,
  page = 0,
  size = 50,
  name = "",
  latitude = null,
  longitude = null,
} = {}) => {
  const token = Cookies.get("accessToken") || localStorage.getItem("accessToken") || localStorage.getItem("token");
  try {
    const params = {
      projectId,
      page,
      size,
    };
    if (name) params.name = name;
    if (latitude != null) params.latitude = latitude;
    if (longitude != null) params.longitude = longitude;

    const response = await axios.get(
      `${baseURL}api/v1/meter-inventory/store/list`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return checkedStoreResponse(response);
  } catch (error) {
    console.error("Error fetching store list:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const getStoreByIdApi = async (id) => {
  const token = Cookies.get("accessToken") || localStorage.getItem("accessToken") || localStorage.getItem("token");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/meter-inventory/store/get`,
      {
        params: { id },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return checkedStoreResponse(response);
  } catch (error) {
    console.error("Error fetching store by id:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const deleteStoreApi = async (id) => {
  const token = Cookies.get("accessToken") || localStorage.getItem("accessToken") || localStorage.getItem("token");
  try {
    const url = `${baseURL}api/v1/meter-inventory/store/delete?id=${encodeURIComponent(id)}`;
    const response = await axios.delete(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response?.data?.header?.code && response.data.header.code !== 600) {
      const errorMsg = apiErrorMessage(response.data, "Failed to delete store");
      throw new Error(errorMsg);
    }

    return checkedStoreResponse(response);
  } catch (error) {
    console.error("Error deleting store:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};


